import { type BellsMultizoneConfig, type BellsZonedConfig, type UnzonedAiConfig, type ZonedAiConfig, routes } from "@remhealth/ai";
import { type LoginOAuthConfiguration, type Zone } from "@remhealth/host";
import { pathCombine } from "@remhealth/ui";

// Loaded from public/config.js
interface StaticConfig {
  AUTH_CLIENT_ID: string;
  MAX_SINCE_LAST_AUTH?: number;
  URL_CONFIG: string;
  DISABLE_SENTRY?: boolean;
  __APP_ZONE?: Zone;
  __APP_VERSION?: string;
  urls?: ZonedAiConfig["urls"];
}

declare const config: Partial<StaticConfig>;

const staticConfig: StaticConfig = {
  // Default values
  AUTH_CLIENT_ID: "dev-bells-web",
  URL_CONFIG: "https://config.remarkable.ai",
  MAX_SINCE_LAST_AUTH: 60,
  __APP_ZONE: undefined, // All zones by default

  // Overridden values
  ...config,
};

export interface UnzonedConfiguration extends UnzonedAiConfig {
  oauth: LoginOAuthConfiguration;
  productionBuild: boolean;
  maximumMinutesSinceLastAuth: number | undefined;
}

export async function fetchUnzonedConfig(app: string): Promise<UnzonedConfiguration> {
  const databaseConfig = await fetchConfig<BellsMultizoneConfig>(app);

  return {
    ...databaseConfig,
    defaultAuthClientId: staticConfig.AUTH_CLIENT_ID,
    zonedAuthClientIds: databaseConfig.authClientId,
    urls: {
      ...databaseConfig.urls,
      config: staticConfig.URL_CONFIG,
      apollo: databaseConfig.urls.apollo,
      virtualScribeApi: databaseConfig.urls.virtualScribeApi,
      virtualScribeStreaming: databaseConfig.urls.virtualScribeStreaming,
    },
    productionBuild: !!import.meta.env.PROD,
    lockedZone: staticConfig.__APP_ZONE,
    version: staticConfig.__APP_VERSION,
    maximumMinutesSinceLastAuth: staticConfig.MAX_SINCE_LAST_AUTH,
    sentryDsn: staticConfig.DISABLE_SENTRY ? undefined : databaseConfig.sentryDsn,
    oauth: {
      authority: databaseConfig.urls.oauth,
      scope:
        "openid offline_access rh.profile remarkable.ai:core mammoth:core hephaestus:core spell:check translate search sync athena:ingest athena:query spell:words:practice spell:words:profile gen-ai:bells phoebe:core settings:all",
      redirectUri: routes.authRedirect,
      postLogoutRedirectUri: routes.root,
      customLogoutEndpoint: pathCombine(databaseConfig.urls.oauth, "/logout"),
      loginCallbackPath: "/auth",
      logoutCallbackPath: "/loggedout",
      additionalParameters: {
        "X-RH-ApplicationID": "bells-web",
        "X-RH-ApplicationVersion": staticConfig.__APP_VERSION ?? "",
      },
    },
    identifierUrls: {
      "carefabric://transaction-id": transactionId =>
        `https://cfes-prod-logging.netsmartcloud.com/_dashboards/app/dashboards?security_tenant=Core%20Services%20Prod%20Dashboards#/view/b756c4e0-90a6-11eb-a43d-4bc54f7f2447?_g=(time:(from:'now-60d',to:'now'))&_a=(query:(language:kuery,query:'transactionId:%22${transactionId}%22'))`,
    },
  };
}

export async function fetchZonedConfig(zone: Zone, app: string): Promise<ZonedAiConfig> {
  const zonedConfig = await fetchConfig<BellsZonedConfig>(app, zone);

  zonedConfig.urls = {
    ...zonedConfig.urls,
    ...staticConfig.urls,
    apollo: zonedConfig.urls.apollo,
    virtualScribeApi: zonedConfig.urls.virtualScribeApi,
    virtualScribeStreaming: zonedConfig.urls.virtualScribeStreaming,
  };

  return zonedConfig;
}

async function fetchConfig<T>(app: string, zone?: Zone): Promise<T> {
  const search = new URLSearchParams();
  search.append("app", app);
  search.append("clientId", staticConfig.AUTH_CLIENT_ID);

  if (zone) {
    search.append("zone", zone);
  }

  const response = await fetch(`${staticConfig.URL_CONFIG}/api/config/public?` + search.toString(), {
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "X-RH-ApplicationID": app,
      "X-RH-ApplicationVersion": staticConfig.__APP_VERSION ?? "",
    },
  });

  if (response.status !== 200) {
    throw new Error(`Unexpected status code ${response.status} when fetching config.`);
  }

  return (await response.json()) as T;
}
